/**
 * Departments table component for organization structure management.
 *
 * This component displays departments in a tabular format with their
 * associated member counts and actions. It provides functionality for
 * adding new departments through an inline button interface.
 *
 * Key Features:
 * - Displays departments with name, member count, and delete action columns
 * - Supports adding new departments via button with proper empty state
 * - Shows appropriate empty states when no departments are present
 * - Integrates with React Table for consistent table rendering
 * - Provides user-friendly interface for department management
 *
 * State Management:
 * - Empty state: Shows message and add department button when no departments exist
 * - Populated state: Shows table with data and add department row at bottom
 *
 * The component uses a "last row children" pattern to render additional UI
 * elements (empty state, add department controls) as table rows for consistent styling.
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus, LoaderIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';

import { DepartmentFormData } from '../../types/department';

/**
 * Props interface for the DepartmentsTable component.
 * Includes table configuration, interaction handlers, and state flags.
 */
interface DepartmentsTableProps {
  /** Configured React Table instance with department data and columns */
  table: Table<DepartmentFormData>;
  /** Callback when add department button is clicked */
  onAddDepartment: () => void;
  /** Whether the table currently has any departments */
  hasDepartments: boolean;
  /** Whether data is currently being loaded */
  isLoading?: boolean;
}

/**
 * Departments table component with integrated department management.
 *
 * This component orchestrates the display of departments in a table format while
 * providing contextual UI for adding new departments. It adapts its presentation based
 * on current state (empty vs populated).
 *
 * Rendering Logic:
 * - Empty state: Full-height message with prominent add department button
 * - Populated state: Data table with add department row at bottom
 *
 * The component uses the DataTable's "lastRowChildren" pattern to seamlessly
 * integrate additional UI elements as table rows, maintaining consistent styling
 * and layout with the rest of the table.
 */
export const DepartmentsTable: React.FC<DepartmentsTableProps> = ({
  table,
  onAddDepartment,
  hasDepartments,
  isLoading = false,
}) => {
  const { t } = useTranslation(['authenticated.settings', 'common']);

  /**
   * Renders the loading state when data is being fetched.
   * Shows a centered loading spinner within the table structure.
   */
  const renderLoadingState = (): React.ReactNode => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="h-32 px-0">
            <StickyTableStateContent align="center" className="px-4 py-3">
              <LoaderIcon className="h-4 w-4 animate-spin text-foreground opacity-50" />
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }
    return null;
  };

  /**
   * Renders the empty state when no departments are present.
   * Shows a left-aligned message with prominent add department button.
   * Only displayed when hasDepartments is false and not loading.
   */
  const renderEmptyState = (): React.ReactNode => {
    if (!hasDepartments && !isLoading) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="px-0 py-4">
            <StickyTableStateContent className="items-start gap-3 px-8 text-left">
              <div className="flex flex-col items-start gap-0.5">
                <div className="text-sm font-semibold text-foreground">
                  {t('pages.departments.emptyState.title')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('pages.departments.emptyState.description')}
                </div>
              </div>
              <Button type="button" onClick={onAddDepartment} className="h-10 gap-2">
                <CirclePlus className="h-4 w-4" />
                {t('pages.departments.addDepartment')}
              </Button>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  /**
   * Renders the add department row when departments exist.
   * Provides a compact add department interface at the bottom of the table.
   * Only shown when departments are present.
   */
  const renderAddDepartmentRow = (): React.ReactNode => {
    if (hasDepartments) {
      return (
        <tr className="hover:bg-transparent">
          <td colSpan={table.getAllColumns().length} className="h-16 px-4">
            <div className="flex justify-start">
              <Button
                type="button"
                variant="ghost"
                onClick={onAddDepartment}
                className="gap-2"
                size="sm"
              >
                <CirclePlus className="h-4 w-4" />
                {t('pages.departments.addDepartment')}
              </Button>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  // Combine all conditional row elements for DataTable integration
  const lastRowChildren = (
    <>
      {renderLoadingState()}
      {renderEmptyState()}
      {renderAddDepartmentRow()}
    </>
  );

  return <DataTable table={table} lastRowChildren={lastRowChildren} />;
};
